function isHexColor(v) {
  return !!(v && /^#[0-9a-fA-F]{6}$/.test(String(v).trim()));
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function hexToRgb(hex) {
  const h = String(hex).trim().replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return { r, g, b };
}

function rgbToHex({ r, g, b }) {
  const to = (x) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}

// RGB [0..255] -> HSL [0..360, 0..100, 0..100]
function rgbToHsl({ r, g, b }) {
  const rr = r / 255;
  const gg = g / 255;
  const bb = b / 255;
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === rr) h = ((gg - bb) / d) % 6;
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

  return {
    h,
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToRgb({ h, s, l }) {
  const ss = clamp(s, 0, 100) / 100;
  const ll = clamp(l, 0, 100) / 100;

  const c = (1 - Math.abs(2 * ll - 1)) * ss;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ll - c / 2;

  let rr = 0, gg = 0, bb = 0;
  if (h >= 0 && h < 60) { rr = c; gg = x; bb = 0; }
  else if (h < 120) { rr = x; gg = c; bb = 0; }
  else if (h < 180) { rr = 0; gg = c; bb = x; }
  else if (h < 240) { rr = 0; gg = x; bb = c; }
  else if (h < 300) { rr = x; gg = 0; bb = c; }
  else { rr = c; gg = 0; bb = x; }

  return {
    r: (rr + m) * 255,
    g: (gg + m) * 255,
    b: (bb + m) * 255,
  };
}

function hashInt(v) {
  const str = String(v ?? '');
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function fallbackProjectColor(projectId) {
  const colors = ['#6B7280', '#9CA3AF', '#818CF8', '#60A5FA', '#34D399', '#F59E0B', '#F472B6'];
  const idx = Math.abs(Number(projectId) || 0) % colors.length;
  return colors[idx];
}

export function deriveProjectColor(clientHex, projectId) {
  if (!isHexColor(clientHex)) return fallbackProjectColor(projectId);

  const base = rgbToHsl(hexToRgb(clientHex));
  const h = base.h;
  const s = clamp(base.s, 35, 90);

  const steps = [-18, -12, -6, 0, 6, 12, 18];
  const idx = hashInt(projectId) % steps.length;
  const l = clamp(base.l + steps[idx], 22, 82);

  return rgbToHex(hslToRgb({ h, s, l }));
}

// Same base rules as deriveProjectColor, but allows selecting a stable variant
// (0..4) instead of hashing by project id.
export function deriveProjectColorVariant(clientHex, projectId, variantIndex) {
  if (!isHexColor(clientHex)) return fallbackProjectColor(projectId);

  const base = rgbToHsl(hexToRgb(clientHex));
  const h = base.h;
  const s = clamp(base.s, 35, 90);

  const v = Number(variantIndex);
  const steps5 = [-12, -6, 0, 6, 12];
  const idx = Number.isFinite(v) ? clamp(Math.floor(v), 0, steps5.length - 1) : 2;
  const l = clamp(base.l + steps5[idx], 22, 82);
  return rgbToHex(hslToRgb({ h, s, l }));
}
